/*
 * @Author: yanghailong
 * @Date: 2023-08-23 09:05:54
 * @Last Modified by: yanghailong
 * @Last Modified time: 2026-03-19 17:49:41
 */
import { Handler } from './handler.js'
import { Storage } from './storage.js'
import { Painter } from './painter.js'
import { Property as EditorProperty } from '../editor/property.js'
import { GraphElement } from './graph-element.js'
import { AddObjectRecord } from './history/record.js'
import { _History } from './history/history.js'
import { createElement, EventDispatcher } from './utils/index.js'
import { WebSocketServer } from './websocket-server.js'
import {
  BusElement,
  ELinkElement,
  EBlockElement,
  PCCElement,
  ESSElement,
  PVElement,
  PCSElement,
  LoadElement,
  TransformerElement,
  SquareElement,
  BreakerElement,
  GroundElement,
  RectElement,
  CircleElement,
  TextElement,
  ChargePileElement,
  ChargeStationElement,
  MeterElement,
  SwitchElement,
  FoldLineElement,
} from './element/index.js'



import { initLib } from "@/topo/editor/lib"
import { DotParser, DotBuilder, DotSerializer } from './dot/index.js'

const Constructors = {
  bus: BusElement,
  eLink: ELinkElement,
  eBlock: EBlockElement,
  pcc: PCCElement,
  pcs: PCSElement,
  ess: ESSElement,
  pv: PVElement,
  payload: LoadElement,
  transformer: TransformerElement,
  square: SquareElement,
  breaker: BreakerElement,
  ground: GroundElement,
  rect: RectElement,
  circle: CircleElement,
  text: TextElement,
  chargePile: ChargePileElement,
  chargeStation: ChargeStationElement,
  meter: MeterElement,
  switch: SwitchElement,
  foldLine: FoldLineElement,
}


export class Topology extends EventDispatcher {

  /**
   * 拓扑图构造器
   * @param {object} option
   * @param {string|HTMLElement} option.root 容器
   * @param {boolean} [option.editor] 是否为编辑器模式
   * @param {string} [option.wsUrl] websocket地址
   */
  constructor(option) {
    const { root, editor = true, wsUrl } = option
    super()
    this.editor = editor
    this.dom = this.#initDom(root)
    this.storage = new Storage(this)
    this.painter = new Painter(this)
    this.history = new _History(this)
    this.handler = new Handler(this)
    this.property = editor ? new EditorProperty(this) : null
    this.wss = wsUrl ? WebSocketServer.Instance(this, wsUrl) : null
  }



  #initDom(root) {
    let rootElem = typeof root == 'string' ? document.querySelector(root) : root
    if (!(rootElem instanceof HTMLElement)) {
      throw new Error('无效的容器元素，无法初始化拓扑图')
    }
    rootElem.classList.add('et-root')


    let graphElem = createElement('div', ['et-graph'], {
      tabIndex: 1
    })

    const domCollection = {
      rootElem,
      graphElem,
    }

    // 创建左侧元件列表DOM
    if (this.editor) {
      let libElem = initLib()
      rootElem.append(libElem)
      domCollection.libElem = libElem
    }

    rootElem.append(graphElem)

    // 创建右侧属性DOM
    if (this.editor) {
      let propsElem = createElement('div', ['et-property'])
      rootElem.append(propsElem)
      domCollection.propsElem = propsElem
    }

    return domCollection
  }

  toJSON() {
    let graph = this.storage.graph
    let edges = graph.edges.map(edge => {
      let source = edge.source
      let target = edge.target
      return {
        uuid: edge.uuid,
        source: {
          node: source.node.uuid,
          port: source.port.attrs.uuid,
        },
        target: {
          node: target.node.uuid,
          port: target.port.attrs.uuid,
        }
      }
    })
    let nodes = graph.nodes.map(node => node.toJSON())
    return {
      edges: edges,
      nodes: nodes,
      version: 2,
    }
  }

  async fromJSON(json) {
    const create = async (data) => {
      let valid = validate(data)
      if (valid) {
        const { nodes, edges } = data
        // 生成元素
        for (const node of nodes) {
          const Ctor = Constructors[node.type] || GraphElement
          await Ctor.fromJSON(this, node)
        }
        // 生成连线
        for (const edge of edges) {
          let sGE = this.storage.getNodeById(edge.source.node)
          let sPort = this.storage.getPortById(edge.source.port)
          //
          let tGE = this.storage.getNodeById(edge.target.node)
          let tPort = this.storage.getPortById(edge.target.port)
          //
          this.storage.graph.addEdge({ node: sGE, port: sPort }, { node: tGE, port: tPort })
        }
        this.painter.paint()
        this.storage.setEditable(this.editor)
      } else {
        console.error('invalid json')
      }
    }

    if (Object.prototype.toString.call(json) === '[object Object]') {
      await create(json)
    } else {
      try {
        let data = JSON.parse(json)
        await create(data)
      } catch (error) {
        console.warn(error)
      }
    }
  }


  addElementByType(type, text, x, y) {
    console.log('type', type)

    if (!type) {
      throw new TypeError('type is required')
    }

    x = x - this.storage.stage.x()
    y = y - this.storage.stage.y()


    let elem = null

    if (type in Constructors) {
      const Ctor = Constructors[type]
      elem = Ctor.create(this, x, y)
    }
    else {
      elem = GraphElement.create(this, type, x, y, {
        text: text,
        port: true
      })
    }

    this.storage.addElement(elem)
    this.history.push(new AddObjectRecord(this, elem))
  }


  /**
 * 更新数据
 * @param {object} dataObject dataObject 是数据集合 如 {uuid: {i_a: 0. u_ab: 0}, uuid2: {i_a: 0. u_ab: 0}}
 */
  updateData(dataObject) {
    for (const elem of this.storage.displayElement) {
      let uuid = elem.deviceUUID
      if (elem instanceof ESSElement || elem instanceof SquareElement) {
        const [_, devUuid] = elem.deviceUUID.split('-')
        uuid = devUuid
      }
      const valueObject = dataObject[uuid] || {}
      if (typeof elem.updateData === 'function') {
        elem.updateData(valueObject, dataObject)
      }
    }

    this.painter.paint()
  }

  /**
   * 将当前拓扑图导出为 DOT 语法字符串
   * @returns {string}
   */
  toDot() {
    const serializer = new DotSerializer(this)
    return serializer.serialize()
  }

  /**
   * 通过自定义语法生成拓扑图
   * @param {string} dot
   * @param {object} [options]
   * @param {boolean} [options.clear=true] 生成前是否清空画布
   * @returns {Promise<void>}
   * @example
   * bus -> breaker -> transformer -> breaker -> pcs -> breaker -> elink(1)
   * bus -> breaker -> transformer -> breaker -> pcs -> breaker -> elink(2)
   * bus -> elink(3) -> eblock(1),eblock(2),eblock(3)
   * bus -> elink(8) -> eblock*100
   */
  async fromDot(dot, options = {}) {
    const { clear = true } = options

    /**
     * 节点名称定义:
     * bus: 母线
     * breaker: 断路器
     * transformer: 变压器
     * pcs: 逆变器
     * elink: 能量链
     * eblock: 能量块
     * load: 负载
     * pcc: 电表
     *
     * 语法说明：
     * 当节点传参时，参数表示其名称，例如 elink(3) 表示 3 号 eLink
     * eblock*10 表示有 10 个 eBlock
     * 同级有多个节点时用逗号或者空格分隔
     */
    const parser = new DotParser()
    const paths = parser.parse(dot)
   

    if (!paths.length) {
      console.warn('[fromDot] 未解析到有效路径')
      return
    }

    if (clear) {
      this.clear()
    }

    const builder = new DotBuilder(this, paths, Constructors)
    await builder.build()
  }


  undo() {
    this.history.undo()
  }

  redo() {
    this.history.redo()
  }
  clear() {
    this.storage.clear()
    this.painter.paint()
  }

  start() {
    this.wss?.startMonitor()
  }

  stop() {
    this.wss?.stopMonitor()
  }
}


function validate(data) {
  // if (data.version != 2) {
  //   console.error('版本不支持, 请使用2版本')
  //   return false
  // }
  if (!data.nodes) {
    console.error('无节点数据')
    return false
  }
  if (!data.edges) {
    console.error('无连线数据')
    return false
  }
  return true
}
